import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { AdminBudgets } from "@/components/admin/AdminBudgets";
import { AdminUsageByUser } from "@/components/admin/AdminUsageByUser";
import { PlatformCosts } from "@/components/admin/PlatformCosts";
import { useToast } from "@/components/shared";
import { formatCurrency, formatNumber } from "@/lib/formatters";
import { adminService } from "@/lib/services";
import type {
  TopAgentEntry,
  TopToolEntry,
  UsageByModel as UsageByModelType,
  UsageByTeam as UsageByTeamType,
  UsageResponse,
  UsageStats,
} from "@/types";

// ── Summary cards ────────────────────────────────────────────────────────────

interface UsageSummaryCardsProps {
  totals: UsageStats;
  totalTokens: number;
  timeRangeLabel: string;
}

function UsageSummaryCards({ totals, totalTokens, timeRangeLabel }: Readonly<UsageSummaryCardsProps>) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <p className="text-sm text-content-tertiary">Total Requests</p>
        <div className="flex items-end justify-between mt-1">
          <p className="text-2xl font-semibold text-content-primary">{formatNumber(totals.requests)}</p>
        </div>
        <p className="text-xs text-content-tertiary mt-1">{timeRangeLabel}</p>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <p className="text-sm text-content-tertiary">Active Users</p>
        <div className="flex items-end justify-between mt-1">
          <p className="text-2xl font-semibold text-content-primary">{totals.activeUsers}</p>
        </div>
        <p className="text-xs text-content-tertiary mt-1">{timeRangeLabel}</p>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <p className="text-sm text-content-tertiary">Tokens Used</p>
        <div className="flex items-end justify-between mt-1">
          <p className="text-2xl font-semibold text-content-primary">{formatNumber(totalTokens)}</p>
        </div>
        <p className="text-xs text-content-tertiary mt-1">{timeRangeLabel}</p>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
        <p className="text-sm text-content-tertiary">Estimated AI Cost</p>
        <div className="flex items-end justify-between mt-1">
          <p className="text-2xl font-semibold text-content-primary">{formatCurrency(totals.estimatedCostUsd)}</p>
        </div>
        <p className="text-xs text-content-tertiary mt-1">{timeRangeLabel}</p>
      </div>
    </div>
  );
}

// ── Usage by team ────────────────────────────────────────────────────────────

interface UsageByTeamProps {
  teams: UsageByTeamType[];
  maxTeamTokens: number;
}

function UsageByTeam({ teams, maxTeamTokens }: Readonly<UsageByTeamProps>) {
  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="mb-4">
        <h3 className="font-medium text-content-primary">Usage by Team</h3>
        <p className="text-xs text-content-tertiary mt-0.5">
          Based on team membership. Users on multiple teams are counted in each.
        </p>
      </div>
      <div className="space-y-4">
        {teams.length === 0 ? (
          <p className="text-center text-content-tertiary py-8">No usage data available</p>
        ) : (
          teams.map((team) => {
            const teamTokens = team.tokensInput + team.tokensOutput;
            const percentage = (teamTokens / maxTeamTokens) * 100;
            return (
              <div key={team.teamName}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-content-primary">{team.teamName}</span>
                  <span className="text-content-tertiary">{formatNumber(teamTokens)} tokens</span>
                </div>
                <div className="h-2 bg-surface-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-brand-gradient rounded-full" style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Usage by model ───────────────────────────────────────────────────────────

interface UsageByModelProps {
  models: UsageByModelType[];
}

function UsageByModel({ models }: Readonly<UsageByModelProps>) {
  if (models.length === 0) return null;

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <h3 className="font-medium text-content-primary mb-4">Usage by Model</h3>
      <div className="space-y-3">
        {models.map((model) => (
          <div key={model.modelName} className="flex items-center justify-between text-sm">
            <span className="text-content-primary">{model.modelName}</span>
            <div className="flex items-center gap-4 text-content-tertiary">
              <span>{formatNumber(model.requests)} requests</span>
              <span>{formatNumber(model.tokensInput + model.tokensOutput)} tokens</span>
              <span className="text-content-primary font-medium">{formatCurrency(model.estimatedCostUsd)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Top tools & agents ───────────────────────────────────────────────────────

interface TopToolsAndAgentsProps {
  topTools: TopToolEntry[];
  topAgents: TopAgentEntry[];
}

function TopToolsAndAgents({ topTools, topAgents }: Readonly<TopToolsAndAgentsProps>) {
  if (topTools.length === 0 && topAgents.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {topTools.length > 0 && (
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <h3 className="font-medium text-content-primary mb-4">Top Tools</h3>
          <div className="space-y-3">
            {topTools.map((tool) => {
              let rateColor = "text-red-400";
              if (tool.successRate >= 0.95) rateColor = "text-green-400";
              else if (tool.successRate >= 0.8) rateColor = "text-yellow-400";
              return (
                <div key={tool.toolName} className="flex items-center justify-between text-sm">
                  <span className="text-content-primary truncate mr-4">{tool.toolName}</span>
                  <div className="flex items-center gap-3 shrink-0 text-content-tertiary">
                    <span>{formatNumber(tool.executions)} calls</span>
                    <span className={rateColor}>{Math.round(tool.successRate * 100)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {topAgents.length > 0 && (
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
          <h3 className="font-medium text-content-primary mb-4">Top Agents</h3>
          <div className="space-y-3">
            {topAgents.map((agent) => (
              <div key={agent.agentName} className="flex items-center justify-between text-sm">
                <span className="text-content-primary truncate mr-4">{agent.agentName}</span>
                <span className="text-content-tertiary shrink-0">{formatNumber(agent.uses)} uses</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function AdminUsage() {
  const { showToast } = useToast();
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d">("30d");
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: "", end: "" });

  useEffect(() => {
    loadUsage();
  }, [timeRange]);

  const loadUsage = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = new Date();

      switch (timeRange) {
        case "7d":
          startDate.setDate(endDate.getDate() - 7);
          break;
        case "30d":
          startDate.setDate(endDate.getDate() - 30);
          break;
        case "90d":
          startDate.setDate(endDate.getDate() - 90);
          break;
      }

      const response = await adminService.getUsage({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
      setDateRange({ start: startDate.toISOString(), end: endDate.toISOString() });
      setUsage(response.data);
    } catch (err) {
      console.error("Failed to load usage:", err);
      showToast("Failed to load usage data", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !usage) {
    return (
      <div className="space-y-6">
        <AdminBudgets />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (!usage) return null;

  const totalTokens = usage.totals.tokensInput + usage.totals.tokensOutput;
  const maxTeamTokens = Math.max(...(usage.byTeam ?? []).map((t) => t.tokensInput + t.tokensOutput), 1);

  const timeRangeLabelMap: Record<string, string> = {
    "7d": "Last 7 days",
    "30d": "Last 30 days",
    "90d": "Last 90 days",
  };
  const timeRangeLabel = timeRangeLabelMap[timeRange] ?? "Last 90 days";

  return (
    <div className="space-y-6">
      {/* Section header with time range selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-content-primary">AI Usage</h3>
          <p className="text-xs text-content-tertiary mt-0.5">Estimated costs based on token usage and model pricing</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-content-tertiary">
          <span>AI Usage range:</span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as "7d" | "30d" | "90d")}
            aria-label="AI Usage date range"
            className="px-3 py-1.5 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </label>
      </div>

      <UsageSummaryCards totals={usage.totals} totalTokens={totalTokens} timeRangeLabel={timeRangeLabel} />

      <UsageByTeam teams={usage.byTeam ?? []} maxTeamTokens={maxTeamTokens} />

      <UsageByModel models={usage.byModel ?? []} />

      <TopToolsAndAgents topTools={usage.topTools ?? []} topAgents={usage.topAgents ?? []} />

      <AdminBudgets />

      <AdminUsageByUser startDate={dateRange.start || undefined} endDate={dateRange.end || undefined} />

      <PlatformCosts />
    </div>
  );
}
